import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { SchemaIO } from '@frontend/schema';
import {
  INFINITE_QUERY_ENDPOINT_NAMES,
  MUTATION_ENDPOINT_NAMES,
  QUERY_ENDPOINT_NAMES,
  STANDARD_QUERY_ENDPOINT_NAMES,
} from './constants';
import { SchemaSchedulerV3Service } from './service';

export type StandardQueryEndpointName = (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number];
export type InfiniteQueryEndpointName = (typeof INFINITE_QUERY_ENDPOINT_NAMES)[number];
export type QueryEndpointName = (typeof QUERY_ENDPOINT_NAMES)[number];
export type ServiceStandardQueries = Pick<typeof SchemaSchedulerV3Service, StandardQueryEndpointName>;
export type ServiceInfiniteQueries = Pick<typeof SchemaSchedulerV3Service, InfiniteQueryEndpointName>;
export type ServiceQueries = Pick<typeof SchemaSchedulerV3Service, QueryEndpointName>;

export type MutationEndpointName = (typeof MUTATION_ENDPOINT_NAMES)[number];
export type ServiceMutations = Pick<typeof SchemaSchedulerV3Service, MutationEndpointName>;

export type ArchiveProviderIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ArchiveProvider']>;
export type GetProviderIO = SchemaIO<(typeof SchemaSchedulerV3Service)['GetProvider']>;
export type ListProvidersIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListProviders']>;
export type ListProvidersMultiIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListProvidersMulti']>;
export type CreateProviderIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateProvider']>;
export type UpdateProviderIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateProvider']>;
export type DeleteProvidersIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteProviders']>;
export type ListMappingsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListMappings']>;
export type DeleteMappingsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteMappings']>;
export type CreateMappingsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateMappings']>;

export type ListCalendarEventsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListCalendarEvents']>;
export type ListCalendarEventsByOwnerIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListCalendarEventsByOwner']>;
export type ListCalendarEventsMultiIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListCalendarEventsMulti']>;
export type GetCalendarEventIO = SchemaIO<(typeof SchemaSchedulerV3Service)['GetCalendarEvent']>;
export type UpdateCalendarEventStatusIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateCalendarEventStatus']>;
export type DeleteCalendarsEventIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteCalendarsEvent']>;
export type CreateCalendarEventIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateCalendarEvent']>;
export type UpdateCalendarEventIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateCalendarEvent']>;

export type ListSchedulesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListSchedules']>;
export type CreateScheduleIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateSchedule']>;
export type UpdateScheduleIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateSchedule']>;
export type DeleteSchedulesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteSchedules']>;

export type ListBookingSubmissionsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListBookingSubmissions']>;
export type GetBookingSubmissionIO = SchemaIO<(typeof SchemaSchedulerV3Service)['GetBookingSubmission']>;
export type CreateBookingSubmissionIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateBookingSubmission']>;
export type UpdateBookingSubmissionIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateBookingSubmission']>;
export type DeleteBookingSubmissionIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteBookingSubmission']>;

export type GetOperatoryIO = SchemaIO<(typeof SchemaSchedulerV3Service)['GetOperatory']>;
export type CreateOperatoryIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateOperatory']>;
export type UpdateOperatoryIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateOperatory']>;
export type DeleteOperatoriesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteOperatories']>;
export type ListOperatoriesMultiIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListOperatoriesMulti']>;
export type ListOperatoriesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListOperatories']>;

export type FeatureFlagStatusIO = SchemaIO<(typeof SchemaSchedulerV3Service)['FeatureFlagStatus']>;
export type ListCalendarAvailabilitiesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListCalendarAvailabilities']>;
export type ListSourceTenantsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListSourceTenants']>;

export type PersonWriteBackIO = SchemaIO<(typeof SchemaSchedulerV3Service)['PersonWriteBack']>;
export type AppointmentStatusWriteBackIO = SchemaIO<(typeof SchemaSchedulerV3Service)['AppointmentStatusWriteBack']>;
export type AppointmentWriteBackIO = SchemaIO<(typeof SchemaSchedulerV3Service)['AppointmentWriteBack']>;

export type UpdateAppointmentTypeIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateAppointmentType']>;
export type CreateAppointmentTypeIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateAppointmentType']>;
export type DeleteAppointmentTypesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['DeleteAppointmentTypes']>;
export type GetAppointmentTypeIO = SchemaIO<(typeof SchemaSchedulerV3Service)['GetAppointmentType']>;
export type ListAppointmentTypesIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListAppointmentTypes']>;
export type ListAppointmentTypesMultiIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListAppointmentTypesMulti']>;
export type ListCalendarEventsByAttendeeIO = SchemaIO<
  (typeof SchemaSchedulerV3Service)['ListCalendarEventsByAttendee']
>;
export type CreateLocationSettingsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['CreateLocationSettings']>;
export type ListLocationSettingsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ListLocationSettings']>;
export type UpdateLocationSettingsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['UpdateLocationSettings']>;

export type ScheduleOnboardingFlowInfo = {
  status: 'not_started' | 'in_progress' | 'completed_appointment_creation' | 'completed' | 'skipped' | 'not_required';
  showOnboardingFlowAfter?: string; // ISO 8601 date string
  practiceVertical?: string;
  locationId?: string;
  contactId?: string;
  providerId?: string;
  appointmentTypeId?: string;
  shouldSetSkipWizardStatus?: boolean;
};
export type BulkUpdateAppointmentTypesColorsIO = SchemaIO<
  (typeof SchemaSchedulerV3Service)['BulkUpdateAppointmentTypesColors']
>;

export type PageConfig = {
  page: number;
  limit: number;
  searchValue?: string;
};
export type SendWaitlistInvitationsIO = SchemaIO<(typeof SchemaSchedulerV3Service)['SendWaitlistInvitations']>;
export type GetWaitlistInvitationDetailsIO = SchemaIO<
  (typeof SchemaSchedulerV3Service)['GetWaitlistInvitationDetails']
>;
export type ConfirmWaitlistInvitationIO = SchemaIO<(typeof SchemaSchedulerV3Service)['ConfirmWaitlistInvitation']>;

export { type PendoTrackingEventName } from './trackingIds';

export enum CalendarEventsEnums {
  APPOINTMENT_EVENT = 'APPOINTMENT_EVENT',
  PROVIDER_OUT_OF_OFFICE_EVENT = 'PROVIDER_OUT_OF_OFFICE_EVENT',
  OFFICE_HOURS_OUT_OF_OFFICE_EVENT = 'OFFICE_HOURS_OUT_OF_OFFICE_EVENT',
  WAITLIST_MESSAGE_EVENT = 'WAITLIST_MESSAGE_EVENT',
}
export type CalendarEventType = {
  startHour: string;
  endHour: string;
  providerName?: string;
  providerId?: string;
  locationId: string;
  isValid: boolean;
  calendarDateValue: string;
  eventId?: string;
  calendarEventType?: CalendarEventsEnums | '';
  startDate?: string;
  endDate?: string;
  name?: string;
  patientId?: string;
  createdBySourceId?: string;
  workstationIds?: string[];
  appointmentType?: string;
  isNonIntegratedSource?: boolean;
  referenceId?: string;
  apptStatusV3?: string;
  backgroundColor?: string; // HEX color
};
export type PatchCalendarEventIO = SchemaIO<(typeof SchemaSchedulerV3Service)['PatchCalendarEvent']>;

export type BookableAppointmentType = {
  id: string;
  locationId: string;
  appointmentTypeId?: string;
  displayName?: string;
  description?: string;
  duration?: number;
  color?: string;
  prepaymentAmount?: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  sourceId?: string;
};

export type BookableAppointmentTypeDetails = {
  ver: number;
  proc: string[];
};

export type AppointmentTypeGroup = {
  appointmentType: AppointmentType;
  bookableAppointmentTypes: BookableAppointmentType[];
  bookableCount: number;
};

export type ListBookableAppointmentTypesGroupedRequest = {
  locationId: string;
  parentLocationId?: string;
  name?: string;
  limit?: number;
  page?: number;
  sourceTenantIds?: string[];
};

export type ListBookableAppointmentTypesGroupedResponse = {
  appointmentTypeGroups: AppointmentTypeGroup[];
  totalAppointmentTypeGroupCount: number;
};

export type DeleteBookableAppointmentTypesRequest = {
  bookableAppointmentTypeIds: string[];
  locationId: string;
};

export type BookableAppointmentTypeBulkUpdate = {
  bookableAppointmentTypeId: string;
  color?: string;
  prepaymentAmount?: number;
};

export type BulkUpdateBookableAppointmentTypesRequest = {
  updates: BookableAppointmentTypeBulkUpdate[];
  locationId: string;
};

export type ValidateBookableAppointmentTypesDeletionRequest = {
  bookableAppointmentTypeIds: string[];
  locationId: string;
};

export type BookableAppointmentTypeDeletionValidation = {
  bookableAppointmentTypeId: string;
  bookableAppointmentTypeName: string;
  canDelete: boolean;
  blockingScheduleDefinitionIds: string[];
  affectedAppointmentCount: number;
};

export type ValidateBookableAppointmentTypesDeletionResponse = {
  validations: BookableAppointmentTypeDeletionValidation[];
};
